// Command Center v2 building blocks.
//
// Six shared sections used by both Dashboard.jsx (desktop) and
// DashboardMobile.jsx (mobile). Each is self-contained: it takes
// state + user (+ a navigate callback where relevant) and does its
// own data fetching where the store doesn't already carry the info.
//
//   <CelebrationStrip />      birthdays + join anniversaries today
//   <CountdownHero />         next match with a ticking countdown +
//                             attendance tally + you-missing CTA
//   <QuickRsvpList />         next 3 matches with inline Yes/Maybe/No
//   <ActivityFeed />          last 5 club events (derived from state)
//   <SplitsRollup />          "owed to you" / "you owe" summary
//   <CricketCareerCard />     stat tiles + season milestone
//
// Nothing here mutates the passed-in state — every write goes through
// the api layer, and reloads bubble up through the parent's reload().

import React, { useEffect, useMemo, useState } from "react";
import {
  Cake, AlertTriangle, Calendar, Clock, MapPin,
  Trophy, Volleyball, CheckCircle2, HelpCircle, XCircle,
  Send, UserPlus, FileEdit, Receipt, Target, TrendingUp,
  Sparkles, Check, ChevronRight
} from "lucide-react";
import { api } from "../api";
import { useToast } from "./Toast";
import Avatar from "./Avatar";
import { easternToMs, todayIsoEastern, fmtInstantEastern } from "../utils/time";

// ─── Shared helpers ─────────────────────────────────────────────────────
const RSVP_OPTIONS = [
  { value: "Available",     label: "Yes",   Icon: CheckCircle2, tone: "yes"   },
  { value: "Tentative",     label: "Maybe", Icon: HelpCircle,   tone: "maybe" },
  { value: "Not Available", label: "No",    Icon: XCircle,      tone: "no"    }
];

// Today's YYYY-MM-DD in Pittsburgh Eastern (not browser local — avoids
// off-by-one "next 3 matches" when a west-coast viewer opens the app).
function todayIso() { return todayIsoEastern(); }

function fmtDay(iso) {
  if (!iso) return { day: "--", weekday: "---" };
  // Format the DATE-ONLY string in Eastern so a browser in another
  // timezone still shows the day the match is actually on.
  const d = new Date(`${iso}T12:00:00-04:00`);   // noon ET, DST-safe
  return {
    day:     new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", day: "2-digit" }).format(d),
    weekday: new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", weekday: "short" }).format(d)
  };
}

function fmtDateLong(iso) {
  if (!iso) return "";
  const d = new Date(`${iso}T12:00:00-04:00`);
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    weekday: "short", month: "short", day: "numeric"
  }).format(d);
}
function fmtTime(hhmm) {
  if (!hhmm) return "TBD";
  const [h, m] = hhmm.split(":").map(Number);
  const hh = ((h + 11) % 12) + 1;
  const ampm = h < 12 ? "AM" : "PM";
  return `${hh}:${String(m).padStart(2, "0")} ${ampm} ET`;
}

// ── Live countdown that ticks every second while the tab is visible.
// Interprets the match's date+time as ET wall clock, then subtracts
// Date.now() so the countdown is correct for viewers in any TZ.
function useCountdown(targetDate, targetTime) {
  const targetMs = useMemo(() => {
    const ms = easternToMs(targetDate, targetTime);
    return Number.isFinite(ms) ? ms : null;
  }, [targetDate, targetTime]);

  const compute = () => {
    if (targetMs == null) return { days: 0, hrs: 0, mins: 0, secs: 0, done: true };
    const diff = Math.max(0, targetMs - Date.now());
    const days = Math.floor(diff / 86_400_000);
    const hrs  = Math.floor((diff % 86_400_000) / 3_600_000);
    const mins = Math.floor((diff % 3_600_000) / 60_000);
    const secs = Math.floor((diff % 60_000) / 1000);
    return { days, hrs, mins, secs, done: diff === 0 };
  };

  const [now, setNow] = useState(compute);
  useEffect(() => {
    setNow(compute());
    const id = setInterval(() => {
      if (document.visibilityState === "visible") setNow(compute());
    }, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [targetMs]);
  return now;
}

// ══════════════════════════════════════════════════════════════════════
// 1. CelebrationStrip — birthdays + anniversaries today
// ══════════════════════════════════════════════════════════════════════
function isSameMonthDay(iso) {
  if (!iso) return false;
  const then = new Date(iso);
  const now  = new Date();
  return then.getUTCMonth() === now.getMonth()
      && then.getUTCDate()  === now.getDate();
}
function yearsAgo(iso) {
  if (!iso) return 0;
  const then = new Date(iso);
  const now  = new Date();
  let y = now.getFullYear() - then.getUTCFullYear();
  if (
    now.getMonth() < then.getUTCMonth() ||
    (now.getMonth() === then.getUTCMonth() && now.getDate() < then.getUTCDate())
  ) y -= 1;
  return y;
}

export function CelebrationStrip({ state }) {
  const items = useMemo(() => {
    const out = [];
    for (const m of state.members || []) {
      if (isSameMonthDay(m.dateOfBirth) && m.dateOfBirth) {
        const age = yearsAgo(m.dateOfBirth);
        out.push({
          kind: "birthday",
          who: m.name,
          copy: age > 0 ? `turns ${age} today` : "birthday today"
        });
      }
      if (isSameMonthDay(m.dateJoined) && m.dateJoined) {
        const yrs = yearsAgo(m.dateJoined);
        if (yrs >= 1) {
          out.push({
            kind: "anniversary",
            who: m.name,
            copy: `joined ${yrs} year${yrs === 1 ? "" : "s"} ago today`
          });
        }
      }
    }
    return out;
  }, [state.members]);

  if (items.length === 0) return null;

  return (
    <section className="cmd-celebrate" aria-label="Today's celebrations">
      <span className="cmd-celebrate-icon" aria-hidden="true">
        <Cake size={18}/>
      </span>
      <div className="cmd-celebrate-copy">
        {items.map((it, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span className="cmd-celebrate-sep">·</span>}
            <strong>{it.who}</strong>
            <span>{it.copy}</span>
          </React.Fragment>
        ))}
      </div>
    </section>
  );
}

// ══════════════════════════════════════════════════════════════════════
// 2. CountdownHero — big match card with ticking clock + RSVP CTA
// ══════════════════════════════════════════════════════════════════════
export function CountdownHero({ match, state, user, navigate, onSetRsvp }) {
  const c = useCountdown(match?.date, match?.time);
  const team = match ? state.teams.find(t => t.id === match.teamId) : null;
  const isCricket = match?.sport === "Cricket";
  const teamName = team?.name || "Team";

  const counts = useMemo(() => {
    if (!match) return { a: 0, t: 0, n: 0, none: 0 };
    const roster = new Set(team?.players || []);
    const rows = (state.availability || []).filter(a => a.matchId === match.id && roster.has(a.userId));
    const total = roster.size;
    return {
      a: rows.filter(r => r.status === "Available").length,
      t: rows.filter(r => r.status === "Tentative").length,
      n: rows.filter(r => r.status === "Not Available").length,
      none: Math.max(0, total - rows.length)
    };
  }, [match, team, state.availability]);

  const myRsvp = useMemo(() => {
    if (!match) return null;
    return (state.availability || []).find(a => a.matchId === match.id && a.userId === user.id) || null;
  }, [match, state.availability, user.id]);

  if (!match) return null;

  return (
    <section className={`cmd-hero cmd-hero--${isCricket ? "cricket" : "volleyball"}`}>
      <div className="cmd-hero-left">
        <div>
          <span className="cmd-eyebrow">
            <Trophy size={12}/>
            Up next · {teamName}
          </span>
          <h2 className="cmd-hero-match">
            vs <em>{match.opponent}</em>
          </h2>
          <div className="cmd-hero-meta">
            <span><Calendar size={12}/> {fmtDateLong(match.date)}</span>
            <span><Clock size={12}/> {fmtTime(match.time)}</span>
            {match.venue && <span><MapPin size={12}/> {match.venue}</span>}
          </div>
        </div>

        <div className="cmd-countdown" aria-label="Time until match">
          <div className="cmd-countdown-cell"><b>{String(c.days).padStart(2, "0")}</b><small>Days</small></div>
          <span className="cmd-countdown-sep">:</span>
          <div className="cmd-countdown-cell"><b>{String(c.hrs).padStart(2, "0")}</b><small>Hrs</small></div>
          <span className="cmd-countdown-sep">:</span>
          <div className="cmd-countdown-cell"><b>{String(c.mins).padStart(2, "0")}</b><small>Min</small></div>
          <span className="cmd-countdown-sep">:</span>
          <div className="cmd-countdown-cell"><b>{String(c.secs).padStart(2, "0")}</b><small>Sec</small></div>
        </div>
      </div>

      <div className="cmd-hero-right">
        <div className="cmd-avail" role="group" aria-label="RSVP tally">
          <div className="cmd-avail-cell cmd-avail-cell--yes"><b>{counts.a}</b><small>Yes</small></div>
          <div className="cmd-avail-cell cmd-avail-cell--maybe"><b>{counts.t}</b><small>Maybe</small></div>
          <div className="cmd-avail-cell cmd-avail-cell--no"><b>{counts.n}</b><small>No</small></div>
          <div className="cmd-avail-cell cmd-avail-cell--pending"><b>{counts.none}</b><small>Pending</small></div>
        </div>

        {!myRsvp ? (
          <div className="cmd-rsvpCard cmd-rsvpCard--missing">
            <div className="cmd-rsvpCard-head">
              <span className="cmd-rsvpCard-icon cmd-rsvpCard-icon--missing" aria-hidden="true">
                <AlertTriangle size={16}/>
              </span>
              <div className="cmd-rsvpCard-text">
                <strong>You haven't RSVP'd yet.</strong>
                <small>Tap one to answer.</small>
              </div>
            </div>
            <div className="cmd-rsvpCard-btns">
              {RSVP_OPTIONS.map(o => (
                <button
                  key={o.value}
                  type="button"
                  className={`cmd-rsvpBtn cmd-rsvpBtn--${o.tone}`}
                  onClick={() => onSetRsvp?.(match.id, o.value)}
                >
                  <o.Icon size={14}/> {o.label}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className={`cmd-rsvpCard cmd-rsvpCard--in cmd-rsvpCard--in-${myRsvp.status === "Available" ? "yes" : myRsvp.status === "Tentative" ? "maybe" : "no"}`}>
            <div className="cmd-rsvpCard-head">
              <span className={`cmd-rsvpCard-icon cmd-rsvpCard-icon--in-${myRsvp.status === "Available" ? "yes" : myRsvp.status === "Tentative" ? "maybe" : "no"}`}>
                <Check size={16} strokeWidth={3}/>
              </span>
              <div className="cmd-rsvpCard-text">
                <strong>
                  You're marked{" "}
                  <em className={`cmd-rsvpCard-answer cmd-rsvpCard-answer--${myRsvp.status === "Available" ? "yes" : myRsvp.status === "Tentative" ? "maybe" : "no"}`}>
                    {myRsvp.status === "Available" ? "Yes" : myRsvp.status === "Tentative" ? "Maybe" : "No"}
                  </em>
                </strong>
                <small>Change your mind?</small>
              </div>
            </div>
            <div className="cmd-rsvpCard-btns cmd-rsvpCard-btns--change">
              {RSVP_OPTIONS.filter(o => o.value !== myRsvp.status).map(o => (
                <button
                  key={o.value}
                  type="button"
                  className={`cmd-rsvpBtn cmd-rsvpBtn--${o.tone}`}
                  onClick={() => onSetRsvp?.(match.id, o.value)}
                >
                  <o.Icon size={14}/> {o.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

// ══════════════════════════════════════════════════════════════════════
// 3. QuickRsvpList — next 3 matches with inline RSVP
// ══════════════════════════════════════════════════════════════════════
export function QuickRsvpList({ state, user, onSetRsvp, navigate }) {
  const nowMs = Date.now();
  const upcoming = useMemo(() => {
    // Filter + sort by actual ET kickoff time so a match that already
    // started today drops off and the RSVP list shows only games the
    // user can still respond to.
    return (state.matches || [])
      .filter(m => (m.status || "Scheduled") === "Scheduled")
      .map(m => ({ m, startMs: easternToMs(m.date, m.time || "12:00") }))
      .filter(x => Number.isFinite(x.startMs) && x.startMs > nowMs)
      .sort((a, b) => a.startMs - b.startMs)
      .slice(0, 3)
      .map(x => x.m);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.matches]);

  if (upcoming.length === 0) return null;

  return (
    <section className="cmd-card" aria-labelledby="cmd-qrsvp-title">
      <div className="cmd-card-head">
        <Calendar size={14}/>
        <h3 id="cmd-qrsvp-title">Quick RSVP · Next 3 matches</h3>
      </div>
      <div className="cmd-card-body">
        <div className="cmd-qrsvp">
          {upcoming.map(m => {
            const my = (state.availability || []).find(a => a.matchId === m.id && a.userId === user.id);
            const { day, weekday } = fmtDay(m.date);
            const isCricket = m.sport === "Cricket";
            return (
              <div
                key={m.id}
                className={`cmd-qrsvp-row${my ? " cmd-qrsvp-row--your" : ""}`}
              >
                <div className="cmd-qrsvp-date"><b>{day}</b><small>{weekday}</small></div>
                <div className={`cmd-qrsvp-info cmd-qrsvp-sport--${isCricket ? "cricket" : "volleyball"}`}>
                  <b>
                    vs {m.opponent}
                    {my && (
                      <span className={`cmd-yourAnswer cmd-yourAnswer--${my.status === "Available" ? "yes" : my.status === "Tentative" ? "maybe" : "no"}`}>
                        <Check size={10} strokeWidth={3}/>
                        {my.status === "Available" ? "You said yes" :
                         my.status === "Tentative" ? "You said maybe" : "You said no"}
                      </span>
                    )}
                  </b>
                  <small>
                    {isCricket ? <Trophy size={11}/> : <Volleyball size={11}/>}
                    {" "}{m.sport} · {m.venue || "TBD"}
                  </small>
                </div>
                <div className="cmd-rsvpBtns">
                  {RSVP_OPTIONS.map(o => {
                    const active = my?.status === o.value;
                    return (
                      <button
                        key={o.value}
                        type="button"
                        className={`cmd-rsvpBtn cmd-rsvpBtn--${o.tone}${active ? " cmd-rsvpBtn--active" : ""}`}
                        disabled={active}
                        onClick={() => onSetRsvp?.(m.id, o.value)}
                      >{o.label}</button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ══════════════════════════════════════════════════════════════════════
// 4. ActivityFeed — derived from state (no new endpoint needed)
// ══════════════════════════════════════════════════════════════════════
function relTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  if (diff < 0) return "just now";
  const mins = Math.floor(diff / 60_000);
  if (mins < 60)   return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)    return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1)  return "yesterday";
  if (days < 7)    return `${days}d ago`;
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    month: "short", day: "numeric"
  }).format(new Date(iso));
}

export function ActivityFeed({ state }) {
  const events = useMemo(() => {
    const out = [];
    // Recently-published or drafted squads
    for (const s of state.squads || []) {
      const m = (state.matches || []).find(x => x.id === s.matchId);
      if (!m) continue;
      out.push({
        kind: s.isPublished ? "squad" : "draft",
        title: s.isPublished ? "Squad announced" : "Draft squad saved",
        detail: `${s.isPublished ? "for" : "for"} vs ${m.opponent}`,
        note: s.isPublished
          ? `${(s.players || []).length} starters + ${(s.substitutes || []).length} subs`
          : "not yet announced",
        when: s.publishedAt || s.updatedAt || null
      });
    }
    // Recently-joined members (last 21 days)
    const joinCut = Date.now() - 21 * 86_400_000;
    for (const m of state.members || []) {
      if (!m.dateJoined) continue;
      const t = new Date(m.dateJoined).getTime();
      if (t >= joinCut) {
        out.push({
          kind: "join",
          title: m.name,
          detail: "joined the club",
          note: null,
          when: m.dateJoined
        });
      }
    }
    // Recent RSVPs (from availability rows if they carry a timestamp)
    for (const a of state.availability || []) {
      if (!a.updatedAt && !a.createdAt) continue;
      const m = (state.matches || []).find(x => x.id === a.matchId);
      const who = (state.members || []).find(u => u.id === a.userId);
      if (!m || !who) continue;
      out.push({
        kind: a.status === "Available" ? "yes" :
              a.status === "Tentative" ? "maybe" : "no",
        title: who.name,
        detail: `RSVP'd ${a.status === "Available" ? "Yes" : a.status === "Tentative" ? "Maybe" : "No"} for vs ${m.opponent}`,
        note: null,
        when: a.updatedAt || a.createdAt
      });
    }
    return out
      .filter(e => e.when)
      .sort((a, b) => (b.when > a.when ? 1 : -1))
      .slice(0, 5);
  }, [state.squads, state.matches, state.members, state.availability]);

  if (events.length === 0) return null;

  const iconOf = (k) => {
    switch (k) {
      case "yes":   return <CheckCircle2 size={13}/>;
      case "maybe": return <HelpCircle size={13}/>;
      case "no":    return <XCircle size={13}/>;
      case "squad": return <Send size={13}/>;
      case "draft": return <FileEdit size={13}/>;
      case "join":  return <UserPlus size={13}/>;
      default:      return <Sparkles size={13}/>;
    }
  };

  return (
    <section className="cmd-card" aria-labelledby="cmd-feed-title">
      <div className="cmd-card-head">
        <Clock size={14}/>
        <h3 id="cmd-feed-title">Recent activity</h3>
      </div>
      <div className="cmd-card-body" style={{ paddingTop: 4 }}>
        <ul className="cmd-feed">
          {events.map((e, i) => (
            <li key={i}>
              <span className={`cmd-feed-icon cmd-feed-icon--${e.kind}`}>
                {iconOf(e.kind)}
              </span>
              <div className="cmd-feed-body">
                <strong>{e.title}</strong>
                <span> {e.detail}</span>
                {e.note && <p>{e.note}</p>}
              </div>
              <span className="cmd-feed-when">{relTime(e.when)}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// ══════════════════════════════════════════════════════════════════════
// 5. SplitsRollup — totals + who owes what
// ══════════════════════════════════════════════════════════════════════
function fmtUSD(n) {
  return `$${(Number(n) || 0).toFixed(2)}`;
}

export function SplitsRollup({ user, navigate }) {
  const [splits, setSplits] = useState([]);
  useEffect(() => {
    let alive = true;
    api.listMySplits()
      .then(r => { if (alive) setSplits(r); })
      .catch(() => { if (alive) setSplits([]); });
    return () => { alive = false; };
  }, []);

  const { owedToYou, youOwe, rows } = useMemo(() => {
    const list = [];
    let toYou = 0, from = 0;
    for (const s of splits) {
      if (s.status === "Closed") continue;
      if (s.paidByUserId === user.id) {
        // Others owe you
        for (const p of s.participants || []) {
          if (p.userId === user.id || p.paid) continue;
          toYou += p.shareAmount;
          list.push({
            who: p.name || "Someone",
            title: s.title,
            amt: p.shareAmount,
            dir: "in"
          });
        }
      } else {
        // You owe the payer
        const mine = (s.participants || []).find(p => p.userId === user.id);
        if (mine && !mine.paid) {
          from += mine.shareAmount;
          list.push({
            who: s.paidByName || "Someone",
            title: s.title,
            amt: mine.shareAmount,
            dir: "out"
          });
        }
      }
    }
    return {
      owedToYou: toYou,
      youOwe:    from,
      rows: list.slice(0, 4)
    };
  }, [splits, user.id]);

  if (splits.length === 0) return null;

  return (
    <section className="cmd-card" aria-labelledby="cmd-splits-title">
      <div className="cmd-card-head">
        <Receipt size={14}/>
        <h3 id="cmd-splits-title">Splits</h3>
      </div>
      <div className="cmd-card-body">
        <div className="cmd-splitsTot">
          <div className="cmd-splitsTot-cell cmd-splitsTot-cell--in">
            <small>Owed to you</small>
            <b>{fmtUSD(owedToYou)}</b>
          </div>
          <div className="cmd-splitsTot-cell cmd-splitsTot-cell--out">
            <small>You owe</small>
            <b>{fmtUSD(youOwe)}</b>
          </div>
        </div>

        {rows.length > 0 && (
          <ul className="cmd-splitsList">
            {rows.map((r, i) => {
              const initials = (r.who || "?").split(/\s+/).slice(0, 2).map(s => s[0]).join("").toUpperCase();
              return (
                <li key={i}>
                  <span className={`cmd-avatarInitials cmd-avatarInitials--${r.dir === "in" ? "in" : "out"}`}>{initials}</span>
                  <span className="cmd-splitsList-name">
                    {r.who} <span>· {r.title}</span>
                  </span>
                  <span className={`cmd-splitsList-amt cmd-splitsList-amt--${r.dir}`}>
                    {r.dir === "in" ? "+" : "−"}{fmtUSD(r.amt)}
                  </span>
                </li>
              );
            })}
          </ul>
        )}

        <button type="button" className="cmd-cta" onClick={() => navigate?.("splits")}>
          Open Splits <ChevronRight size={12}/>
        </button>
      </div>
    </section>
  );
}

// ══════════════════════════════════════════════════════════════════════
// 6. CricketCareerCard — stats + milestone
// ══════════════════════════════════════════════════════════════════════
const MILESTONE_STEP = 5;   // pick the nearest 5-mark for each metric

function nextMilestone(value) {
  if (value == null) return null;
  const next = Math.ceil((value + 1) / MILESTONE_STEP) * MILESTONE_STEP;
  return { next, away: next - value };
}

export function CricketCareerCard({ user, state, navigate }) {
  // Pick the user's first Cricket team, if any
  const cricketTeam = useMemo(() => {
    return (state.teams || []).find(t =>
      t.sport === "Cricket" && (t.players || []).includes(user.id)
    ) || null;
  }, [state.teams, user.id]);

  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!cricketTeam) { setStats(null); return; }
    let alive = true;
    api.getPlayerStats(user.id, cricketTeam.id)
      .then(r => { if (alive) setStats(r); })
      .catch(() => { if (alive) setStats(null); });
    return () => { alive = false; };
  }, [cricketTeam?.id, user.id]);

  if (!cricketTeam) return null;

  const matches = stats?.matchesPlayed ?? 0;
  const runs    = stats?.runsScored ?? 0;
  const wickets = stats?.wicketsTaken ?? 0;
  const catches = stats?.catchesTaken ?? 0;

  // Pick the metric closest to its next milestone for the badge line
  const candidates = [
    { label: "runs",    value: runs,    ms: nextMilestone(runs) },
    { label: "wickets", value: wickets, ms: nextMilestone(wickets) },
    { label: "catches", value: catches, ms: nextMilestone(catches) },
    { label: "matches", value: matches, ms: nextMilestone(matches) }
  ].filter(c => c.value > 0);
  const milestone = candidates.sort((a, b) => a.ms.away - b.ms.away)[0];

  return (
    <section className="cmd-card" aria-labelledby="cmd-stats-title">
      <div className="cmd-card-head">
        <TrendingUp size={14}/>
        <h3 id="cmd-stats-title">Your Cricket Career</h3>
        <span className="cmd-card-badge">{cricketTeam.name}</span>
      </div>
      <div className="cmd-card-body">
        <div className="cmd-statsGrid">
          <div className="cmd-stat cmd-stat--matches">
            <b>{matches}</b>
            <small>Matches</small>
          </div>
          <div className="cmd-stat cmd-stat--runs">
            <b>{runs}</b>
            <small>Runs</small>
          </div>
          <div className="cmd-stat cmd-stat--wickets">
            <b>{wickets}</b>
            <small>Wickets</small>
          </div>
          <div className="cmd-stat cmd-stat--catches">
            <b>{catches}</b>
            <small>Catches</small>
          </div>
        </div>

        {milestone && (
          <div className="cmd-milestone">
            <span className="cmd-milestone-icon"><Target size={16}/></span>
            <div className="cmd-milestone-copy">
              {milestone.ms.away} {milestone.label} away from {milestone.ms.next}
              <small>Keep it rolling this season.</small>
            </div>
          </div>
        )}

        <button
          type="button"
          className="cmd-cta"
          onClick={() => navigate?.("rankings")}
        >Full profile <ChevronRight size={12}/></button>
      </div>
    </section>
  );
}
